import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Package, 
  Users, 
  FileText, 
  MessageSquare, 
  Settings,
  Home,
  Plus,
  Edit,
  Trash2,
  Upload,
  Download,
  ToggleLeft,
  ToggleRight,
  Search,
  Eye,
  LogOut,
  UserCog
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { productsAPI, clientsAPI, inquiriesAPI, settingsAPI } from '../services/api';
import ProductFormDialog from '../components/admin/ProductFormDialog';
import ClientFormDialog from '../components/admin/ClientFormDialog';
import ContentSettings from '../components/admin/ContentSettings';
import UserManagement from '../components/admin/UserManagement';
import SEO from '../components/SEO';

const AdminDashboard = () => {
  const { toast } = useToast();
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isProductDialogOpen, setIsProductDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isClientDialogOpen, setIsClientDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [chatEnabled, setChatEnabled] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Real data states from API
  const [products, setProducts] = useState([]);
  const [clientsList, setClientsList] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [settings, setSettings] = useState({});
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalClients: 0,
    newInquiries: 0
  });

  // Fetch all data on mount
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [productsData, clientsData, inquiriesData, settingsData] = await Promise.all([
        productsAPI.getAll(),
        clientsAPI.getAll(),
        inquiriesAPI.getAll(),
        settingsAPI.getAll()
      ]);
      
      setProducts(productsData);
      setClientsList(clientsData);
      setInquiries(inquiriesData);
      setSettings(settingsData);
      setChatEnabled(settingsData.chat_enabled === 'true');
      
      // Update stats
      setStats({
        totalProducts: productsData.length,
        totalClients: clientsData.length,
        newInquiries: inquiriesData.filter(i => i.status === 'new').length
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load dashboard data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await productsAPI.delete(id);
      setProducts(products.filter(p => p.id !== id));
      toast({ title: 'Product deleted successfully' });
    } catch (error) {
      console.error('Error deleting product:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete product',
        variant: 'destructive'
      });
    }
  };

  const handleEditProduct = (product) => {
    setSelectedProduct(product);
    setIsProductDialogOpen(true);
  };

  const handleAddProduct = () => {
    setSelectedProduct(null);
    setIsProductDialogOpen(true);
  };

  const handleSaveProduct = async (productData) => {
    try {
      if (selectedProduct) {
        // Edit existing
        const updated = await productsAPI.update(selectedProduct.id, productData);
        setProducts(products.map(p => p.id === selectedProduct.id ? updated : p));
        toast({ title: 'Product updated successfully' });
      } else {
        // Add new
        const newProduct = await productsAPI.create(productData);
        setProducts([...products, newProduct]);
        toast({ title: 'Product created successfully' });
      }
      setIsProductDialogOpen(false);
      setSelectedProduct(null);
    } catch (error) {
      console.error('Error saving product:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save product',
        variant: 'destructive'
      });
    }
  };

  const handleEditClient = (client) => {
    setSelectedClient(client);
    setIsClientDialogOpen(true);
  };

  const handleSaveClient = async (clientData) => {
    try {
      if (selectedClient) {
        // Update existing client
        const updated = await clientsAPI.update(selectedClient.id, clientData);
        setClientsList(clientsList.map(c => c.id === selectedClient.id ? updated : c));
        toast({ title: 'Client updated successfully' });
      } else {
        // Add new client
        const newClient = await clientsAPI.create(clientData);
        setClientsList([...clientsList, newClient]);
        toast({ title: 'Client created successfully' });
      }
      setIsClientDialogOpen(false);
      setSelectedClient(null);
    } catch (error) {
      console.error('Error saving client:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save client',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteClient = async (id) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    
    try {
      await clientsAPI.delete(id);
      setClientsList(clientsList.filter(c => c.id !== id));
      toast({ title: 'Client deleted successfully' });
    } catch (error) {
      console.error('Error deleting client:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete client',
        variant: 'destructive'
      });
    }
  };

  const handleChatToggle = async (enabled) => {
    try {
      await settingsAPI.update('chat_enabled', enabled.toString());
      setChatEnabled(enabled);
      toast({ 
        title: enabled ? 'Chat widget enabled' : 'Chat widget disabled',
        description: enabled ? 'Chat widget will appear on website' : 'Chat widget is now hidden'
      });
    } catch (error) {
      console.error('Error updating chat setting:', error);
      toast({
        title: 'Error',
        description: 'Failed to update chat setting',
        variant: 'destructive'
      });
    }
  };

  const handleInquiryStatus = async (inquiryId, newStatus) => {
    try {
      await inquiriesAPI.updateStatus(inquiryId, newStatus);
      setInquiries(inquiries.map(i => 
        i.id === inquiryId ? { ...i, status: newStatus } : i
      ));
      toast({ title: `Inquiry marked as ${newStatus}` });
    } catch (error) {
      console.error('Error updating inquiry status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update inquiry status',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteInquiry = async (inquiryId) => {
    if (!window.confirm('Are you sure you want to delete this inquiry?')) return;
    
    try {
      await inquiriesAPI.delete(inquiryId);
      setInquiries(inquiries.filter(i => i.id !== inquiryId));
      toast({ title: 'Inquiry deleted successfully' });
    } catch (error) {
      console.error('Error deleting inquiry:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete inquiry',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Admin Dashboard"
        description="Manalogi Admin Dashboard"
        noindex={true}
        nofollow={true}
      />
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10 shadow-sm">
        <div className="px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">M</span>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Manalogi Admin</h1>
              <p className="text-sm text-gray-500">Welcome, {user?.name || 'Admin'}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Link to="/">
              <Button variant="outline">
                <Home className="w-4 h-4 mr-2" />
                Back to Website
              </Button>
            </Link>
            <Button 
              variant="outline" 
              onClick={() => {
                logout();
                navigate('/login');
                toast({ title: 'Logged out successfully' });
              }}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-white border-r min-h-screen p-6">
          <nav className="space-y-2">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'dashboard' ? 'bg-cyan-50 text-cyan-600' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <LayoutDashboard className="w-5 h-5" />
              <span className="font-medium">Dashboard</span>
            </button>
            <button
              onClick={() => setActiveTab('products')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'products' ? 'bg-cyan-50 text-cyan-600' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Package className="w-5 h-5" />
              <span className="font-medium">Products</span>
            </button>
            <button
              onClick={() => setActiveTab('clients')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'clients' ? 'bg-cyan-50 text-cyan-600' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Users className="w-5 h-5" />
              <span className="font-medium">Clients</span>
            </button>
            <button
              onClick={() => setActiveTab('inquiries')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'inquiries' ? 'bg-cyan-50 text-cyan-600' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <MessageSquare className="w-5 h-5" />
              <span className="font-medium">Inquiries</span>
              {stats.newInquiries > 0 && (
                <Badge variant="destructive" className="ml-auto">{stats.newInquiries}</Badge>
              )}
            </button>
            <button
              onClick={() => setActiveTab('content')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'content' ? 'bg-cyan-50 text-cyan-600' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FileText className="w-5 h-5" />
              <span className="font-medium">Content</span>
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'settings' ? 'bg-cyan-50 text-cyan-600' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Settings className="w-5 h-5" />
              <span className="font-medium">Settings</span>
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'users' ? 'bg-cyan-50 text-cyan-600' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <UserCog className="w-5 h-5" />
              <span className="font-medium">Users</span>
            </button>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8">
          {/* Dashboard */}
          {activeTab === 'dashboard' && (
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-gray-900">Dashboard</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-gray-600">Total Products</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-cyan-600">{products.length}</div>
                    <p className="text-xs text-gray-500 mt-1">BFSI + Industry</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-gray-600">Total Clients</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-cyan-600">{clientsList.length}</div>
                    <p className="text-xs text-gray-500 mt-1">Active partnerships</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-gray-600">New Inquiries</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-orange-600">
                      {inquiries.filter(i => i.status === 'new').length}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Pending response</p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Recent Inquiries</CardTitle>
                  <CardDescription>Latest contact form submissions</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {inquiries.slice(0, 3).map((inquiry) => (
                      <div key={inquiry.id} className="flex items-start justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className="font-semibold text-gray-900">{inquiry.name}</h4>
                            <Badge variant={inquiry.status === 'new' ? 'destructive' : 'secondary'}>
                              {inquiry.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 mb-1">{inquiry.email} • {inquiry.company}</p>
                          <p className="text-sm text-gray-700">{inquiry.message}</p>
                        </div>
                        <Button variant="ghost" size="sm">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Products Management */}
          {activeTab === 'products' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-3xl font-bold text-gray-900">Products Management</h2>
                <Button 
                  onClick={handleAddProduct}
                  className="bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Product
                </Button>
              </div>

              <div className="flex items-center space-x-4 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select defaultValue="all">
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Filter by category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="bfsi">BFSI</SelectItem>
                    <SelectItem value="industry">Various Industry</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Tabs defaultValue="bfsi" className="w-full">
                <TabsList className="grid w-full grid-cols-2 max-w-md">
                  <TabsTrigger value="bfsi">BFSI Products ({products.filter(p => p.category === 'bfsi').length})</TabsTrigger>
                  <TabsTrigger value="industry">Industry Products ({products.filter(p => p.category === 'industry').length})</TabsTrigger>
                </TabsList>
                
                <TabsContent value="bfsi" className="space-y-4 mt-6">
                  {products.filter(p => p.category === 'bfsi').map((product) => (
                    <Card key={product.id}>
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">{product.name_en}</h3>
                            <p className="text-sm text-gray-600 mb-2">{product.name_id}</p>
                            <p className="text-sm text-gray-700 mb-3">{product.description_en}</p>
                            <div className="flex gap-4 text-xs text-gray-500">
                              <span>Features: {product.features?.length || 0}</span>
                              <span>Benefits: {product.benefits?.length || 0}</span>
                              <span>Tech Specs: {product.technical_specs ? 'Yes' : 'No'}</span>
                              <span className={product.image_url ? 'text-green-600' : ''}>
                                Image: {product.image_url ? '✓' : '✗'}
                              </span>
                              <span className={product.brochure_url ? 'text-green-600' : ''}>
                                Brochure: {product.brochure_url ? '✓' : '✗'}
                              </span>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleEditProduct(product)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleDeleteProduct(product.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </TabsContent>
                
                <TabsContent value="industry" className="space-y-4 mt-6">
                  {products.filter(p => p.category === 'industry').map((product) => (
                    <Card key={product.id}>
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">{product.name_en}</h3>
                            <p className="text-sm text-gray-600 mb-2">{product.name_id}</p>
                            <p className="text-sm text-gray-700 mb-3">{product.description_en}</p>
                            <div className="flex gap-4 text-xs text-gray-500">
                              <span>Features: {product.features?.length || 0}</span>
                              <span>Benefits: {product.benefits?.length || 0}</span>
                              <span>Tech Specs: {product.technical_specs ? 'Yes' : 'No'}</span>
                              <span className={product.image_url ? 'text-green-600' : ''}>
                                Image: {product.image_url ? '✓' : '✗'}
                              </span>
                              <span className={product.brochure_url ? 'text-green-600' : ''}>
                                Brochure: {product.brochure_url ? '✓' : '✗'}
                              </span>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleEditProduct(product)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleDeleteProduct(product.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </TabsContent>
              </Tabs>
            </div>
          )}

          {/* Clients Management */}
          {activeTab === 'clients' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-3xl font-bold text-gray-900">Clients Management</h2>
                <Button 
                  onClick={() => {
                    setSelectedClient(null);
                    setIsClientDialogOpen(true);
                  }}
                  className="bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Client
                </Button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {clientsList.map((client) => (
                  <Card key={client.id}>
                    <CardContent className="p-6">
                      <div className="text-center">
                        {client.logo_url ? (
                          <div className="w-full h-24 bg-gray-50 rounded-lg flex items-center justify-center mb-4 p-4">
                            <img
                              src={client.logo_url}
                              alt={client.name}
                              className="max-w-full max-h-full object-contain"
                            />
                          </div>
                        ) : (
                          <div className="w-full h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center mb-4">
                            <span className="text-sm font-semibold text-gray-600">{client.name}</span>
                          </div>
                        )}
                        <h3 className="font-semibold text-gray-900 mb-2">{client.name}</h3>
                        <p className="text-xs text-gray-500 mb-4">Order: {client.display_order}</p>
                        <div className="flex space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="flex-1"
                            onClick={() => handleEditClient(client)}
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDeleteClient(client.id)}
                            className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Inquiries Management */}
          {activeTab === 'inquiries' && (
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-gray-900">Inquiries Management</h2>
              
              <div className="space-y-4">
                {inquiries.map((inquiry) => (
                  <Card key={inquiry.id}>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">{inquiry.name}</h3>
                            <Badge variant={inquiry.status === 'new' ? 'destructive' : 'secondary'}>
                              {inquiry.status}
                            </Badge>
                          </div>
                          <div className="space-y-1 mb-3">
                            <p className="text-sm text-gray-600">Email: {inquiry.email}</p>
                            {inquiry.company && <p className="text-sm text-gray-600">Company: {inquiry.company}</p>}
                            <p className="text-sm text-gray-600">Date: {new Date(inquiry.created_at).toLocaleDateString()}</p>
                          </div>
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <p className="text-sm text-gray-700">{inquiry.message}</p>
                          </div>
                        </div>
                        <div className="flex flex-col space-y-2 ml-4">
                          <Select
                            value={inquiry.status}
                            onValueChange={(value) => handleInquiryStatus(inquiry.id, value)}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="new">New</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="replied">Replied</SelectItem>
                              <SelectItem value="closed">Closed</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDeleteInquiry(inquiry.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Content Settings */}
          {activeTab === 'content' && (
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-gray-900">Content Management</h2>
              <p className="text-gray-600">Manage website content including statistics, about section, solutions, and capabilities</p>
              
              <ContentSettings />
            </div>
          )}

          {/* Settings */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-gray-900">Settings</h2>
              
              <Card>
                <CardHeader>
                  <CardTitle>Company Logo</CardTitle>
                  <CardDescription>Upload and manage your company logo</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <Label>Current Logo</Label>
                      <div className="mt-2 p-4 border-2 border-dashed rounded-lg bg-gray-50">
                        {localStorage.getItem('site_logo_url') ? (
                          <div className="flex items-center justify-between">
                            <img 
                              src={localStorage.getItem('site_logo_url')} 
                              alt="Company Logo" 
                              className="h-16 w-auto object-contain"
                            />
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                localStorage.removeItem('site_logo_url');
                                toast({ title: 'Logo removed successfully' });
                                window.location.reload();
                              }}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Remove
                            </Button>
                          </div>
                        ) : (
                          <div className="text-center py-4">
                            <p className="text-sm text-gray-500">No logo uploaded</p>
                            <p className="text-xs text-gray-400 mt-1">Default logo will be used</p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <Label>Upload New Logo</Label>
                      <div className="mt-2">
                        <Input 
                          type="file" 
                          accept="image/png,image/jpeg,image/svg+xml"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              // Create preview URL (in production, upload to server)
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                localStorage.setItem('site_logo_url', reader.result);
                                toast({ title: 'Logo uploaded successfully' });
                                window.location.reload();
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Recommended: PNG or SVG, max 2MB, transparent background
                        </p>
                      </div>
                    </div>

                    <div>
                      <Label>Company Name (fallback)</Label>
                      <Input 
                        defaultValue={localStorage.getItem('site_company_name') || 'manalogi'}
                        onChange={(e) => {
                          localStorage.setItem('site_company_name', e.target.value);
                        }}
                        placeholder="Enter company name"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Displayed when logo is not uploaded
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Chat Widget</CardTitle>
                  <CardDescription>Control the visibility of the chat widget on your landing page</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {chatEnabled ? (
                        <ToggleRight className="w-6 h-6 text-green-600" />
                      ) : (
                        <ToggleLeft className="w-6 h-6 text-gray-400" />
                      )}
                      <div>
                        <p className="font-medium text-gray-900">Chat Widget Status</p>
                        <p className="text-sm text-gray-600">
                          {chatEnabled ? 'Currently visible on landing page' : 'Currently hidden from landing page'}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={chatEnabled}
                      onCheckedChange={handleChatToggle}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Language Settings</CardTitle>
                  <CardDescription>Configure default language and available translations</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <Label>Default Language</Label>
                      <Select defaultValue="id">
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="id">Indonesian (Bahasa Indonesia)</SelectItem>
                          <SelectItem value="en">English</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Contact Information</CardTitle>
                  <CardDescription>Update your company contact details</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <Label>Email</Label>
                      <Input type="email" defaultValue="halo@manalogi.com" />
                    </div>
                    <div>
                      <Label>Website</Label>
                      <Input type="text" defaultValue="manalogi.com" />
                    </div>
                    <Button className="bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700">
                      Save Changes
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Users Management Tab */}
          {activeTab === 'users' && (
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-gray-900">User Management</h2>
              <UserManagement />
            </div>
          )}
        </main>
      </div>

      {/* Product Form Dialog */}
      <ProductFormDialog
        open={isProductDialogOpen}
        onOpenChange={setIsProductDialogOpen}
        product={selectedProduct}
        onSave={handleSaveProduct}
      />

      {/* Client Form Dialog */}
      <ClientFormDialog
        open={isClientDialogOpen}
        onOpenChange={setIsClientDialogOpen}
        client={selectedClient}
        onSave={handleSaveClient}
      />
    </div>
  );
};

export default AdminDashboard;
